import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  Stack,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import {
  VerifiedUser,
  Close,
  Settings,
  PersonAdd
} from '@mui/icons-material';
import InlineEditableField from './InlineEditableField';
import OnboardingStatusChip from './OnboardingStatusChip';
import ComprehensiveOnboardingView from './ComprehensiveOnboardingView';
import MissingFieldsPanel from './MissingFieldsPanel';
import { analyzeProfileCompletion } from '../utils/ProfileCompletionTracker';
import customerService from '../services/customerService';
import axios from 'axios';

const EditableMaidDetailsModal = ({
  open,
  onClose,
  maidProfile,
  onProfileUpdate,
  onVerifyMaid,
  comprehensiveData,
  loadingComprehensive
}) => {
  const navigate = useNavigate();
  const [editMode] = useState(true);
  const [localMaid, setLocalMaid] = useState(null);
  const [verifyingMaid, setVerifyingMaid] = useState(false);
  const [showMissingFields, setShowMissingFields] = useState(false);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [loadingServiceAreas, setLoadingServiceAreas] = useState(false);
  const [maidServiceAreas, setMaidServiceAreas] = useState([]);
  const [serviceAreasError, setServiceAreasError] = useState('');
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [customerCreationResult, setCustomerCreationResult] = useState(null);

  // Update local maid data when prop changes
  useEffect(() => {
    if (maidProfile) {
      setLocalMaid({ ...maidProfile });
    }
  }, [maidProfile]);

  // Fetch service areas when modal opens
  useEffect(() => {
    const fetchServiceAreas = async () => {
      if (!open) return;
      
      setLoadingServiceAreas(true);
      try {
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
        
        // Fetch all available service areas
        const serviceAreasResponse = await axios.get(`${apiBase}/api/service-areas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (serviceAreasResponse.data.success) {
          setServiceAreas(serviceAreasResponse.data.data || []);
        }

        // Use definedServiceAreas from maid profile if available, otherwise fetch from API
        if (localMaid?.definedServiceAreas && localMaid.definedServiceAreas.length > 0) {
          setMaidServiceAreas(localMaid.definedServiceAreas);
        } else if (localMaid?.id) {
          const maidServiceAreasResponse = await axios.get(`${apiBase}/api/service-areas/maid/${localMaid.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (maidServiceAreasResponse.data.success) {
            setMaidServiceAreas(maidServiceAreasResponse.data.data || []);
          }
        }
      } catch (error) {
        console.error('Error fetching service areas:', error);
        setServiceAreas([]);
        setMaidServiceAreas([]);
        setServiceAreasError('Failed to load service areas. Please refresh the page.');
      } finally {
        setLoadingServiceAreas(false);
      }
    };

    fetchServiceAreas();
  }, [open, localMaid?.id, localMaid?.definedServiceAreas]);

  const handleFieldSave = async (fieldPath, value) => {
    // Handle service areas specially with dedicated API
    if (fieldPath === 'serviceAreas') {
      try {
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
        
        // Convert service area names back to IDs for API call
        const serviceAreaIds = value.map(areaName => {
          const area = serviceAreas.find(sa => sa.name === areaName);
          return area ? area.id : null;
        }).filter(id => id !== null);
        
        const response = await axios.post(`${apiBase}/api/service-areas/maid/${localMaid.id}`, {
          serviceAreaIds: serviceAreaIds
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          setMaidServiceAreas(response.data.data || []);
          
          // Update local maid with service area names for display
          const updatedMaid = { 
            ...localMaid, 
            serviceAreas: value,
            preferredServiceAreas: value 
          };
          setLocalMaid(updatedMaid);
          
          if (onProfileUpdate) {
            onProfileUpdate(updatedMaid);
          }
        }
      } catch (error) {
        console.error('Error updating service areas:', error);
      }
      return;
    }
    
    // Handle other fields normally
    const updatedMaid = { ...localMaid };
    
    if (fieldPath.includes('.')) {
      const [parentField, childField] = fieldPath.split('.');
      if (!updatedMaid[parentField]) {
        updatedMaid[parentField] = {};
      }
      updatedMaid[parentField][childField] = value;
    } else {
      updatedMaid[fieldPath] = value;
    }
    
    setLocalMaid(updatedMaid);
    
    // Notify parent component
    if (onProfileUpdate) {
      onProfileUpdate(updatedMaid);
    }
  };

  const handleEditDetails = () => {
    if (!localMaid) return;
    
    // Navigate to maid onboarding wizard in admin mode
    navigate(`/maid-onboarding?maidId=${localMaid.id}`);
  };

  const handleVerifyMaid = async () => {
    if (!localMaid) return;
    
    setVerifyingMaid(true);
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
      
      const response = await axios.patch(
        `${apiBase}/api/maids/${localMaid.id}/verify`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        const updatedMaid = {
          ...localMaid,
          onboardingStatus: 'approved',
          isVerified: true,
          user: {
            ...localMaid.user,
            isVerified: true
          }
        };
        
        setLocalMaid(updatedMaid);
        
        if (onVerifyMaid) {
          onVerifyMaid(updatedMaid);
        }
      }
    } catch (error) {
      console.error('Error verifying maid:', error);
    } finally {
      setVerifyingMaid(false);
    }
  };

  const handleCreateFakeCustomer = async () => {
    if (!localMaid) return;
    
    setCreatingCustomer(true);
    setCustomerCreationResult(null);
    
    try {
      const result = await customerService.createFakeCustomerFromMaid(localMaid);
      
      setCustomerCreationResult({
        success: true,
        message: 'Test customer created successfully in HousecallPro!',
        customer: result.customer
      });
      
      // Auto-dismiss success message after 5 seconds
      setTimeout(() => {
        setCustomerCreationResult(null);
      }, 5000);
      
    } catch (error) {
      console.error('Error creating fake customer:', error);
      
      setCustomerCreationResult({
        success: false,
        message: error.message || 'Failed to create test customer. Please try again.',
        error
      });
      
      // Auto-dismiss error message after 10 seconds
      setTimeout(() => {
        setCustomerCreationResult(null);
      }, 10000);
    } finally {
      setCreatingCustomer(false);
    }
  };

  const serviceOptions = [
    'Standard Cleaning',
    'Deep Cleaning', 
    'Move In/Out',
    'Post Construction',
    'Vacation Rental',
    'Commercial Cleaning',
    'Carpet Cleaning',
    'Window Cleaning',
    'Pressure Washing'
  ];

  // Service area options are now loaded dynamically from API
  const serviceAreaOptions = serviceAreas.map(area => area.name);

  const languageOptions = [
    'English',
    'Spanish',
    'French',
    'Portuguese',
    'Italian',
    'German',
    'Russian',
    'Chinese',
    'Japanese',
    'Arabic'
  ];

  const workingPreferenceOptions = [
    { value: 'alone', label: 'Alone' },
    { value: 'with_partner', label: 'With Partner' },
    { value: 'no_preference', label: 'No Preference' }
  ];

  const experienceLevelOptions = [
    'Beginner',
    'Intermediate', 
    'Experienced',
    'Expert'
  ];

  if (!localMaid) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            Service Provider Profile
          </Typography>
          <Stack direction="row" spacing={1}>
          </Stack>
        </Stack>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box>
          {/* Customer Creation Result Alert */}
          {customerCreationResult && (
            <Alert 
              severity={customerCreationResult.success ? 'success' : 'error'} 
              sx={{ mb: 3 }}
              onClose={() => setCustomerCreationResult(null)}
            >
              <strong>
                {customerCreationResult.success ? 'Success!' : 'Error:'}
              </strong> {customerCreationResult.message}
              {customerCreationResult.customer && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Customer ID: {customerCreationResult.customer.id}
                </Typography>
              )}
            </Alert>
          )}
          {/* Header Section */}
          <Stack direction="row" alignItems="center" spacing={3} mb={3}>
            <Avatar sx={{ width: 80, height: 80, fontSize: 36 }} src={localMaid.profilePicture}>
              {localMaid.firstName?.[0] || localMaid.user?.firstName?.[0]}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h4" fontWeight={700}>
                {localMaid.firstName || localMaid.user?.firstName} {localMaid.lastName || localMaid.user?.lastName}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Service Provider Profile
              </Typography>
              <Stack direction="row" spacing={1} mt={1}>
                <Chip label={`Rating: ${localMaid.rating || 0}`} color="success" />
                <Chip label={`${localMaid.reviews || 0} Reviews`} />
                {localMaid.onboardingCompletedAt && (
                  <Chip label={`Onboarded: ${localMaid.onboardingCompletedAt}`} color="info" />
                )}
              </Stack>
            </Box>
          </Stack>

          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Edit Mode Active:</strong> Click the edit icon next to any field to modify it. Changes are saved automatically.
          </Alert>

          <Divider sx={{ my: 3 }} />

          {/* Personal Information Section */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Personal Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="First Name"
                  value={localMaid.firstName || localMaid.user?.firstName}
                  fieldPath="user.firstName"
                  type="text"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                  validation={{ required: true, minLength: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Last Name"
                  value={localMaid.lastName || localMaid.user?.lastName}
                  fieldPath="user.lastName"
                  type="text"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                  validation={{ required: true, minLength: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Email"
                  value={localMaid.email || localMaid.user?.email}
                  fieldPath="user.email"
                  type="email"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                  validation={{ required: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Phone Number"
                  value={localMaid.phoneNumber || localMaid.user?.phoneNumber}
                  fieldPath="user.phoneNumber"
                  type="phone"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Date of Birth"
                  value={localMaid.dateOfBirth}
                  fieldPath="personalInfo.dateOfBirth"
                  type="date"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Languages Spoken"
                  value={localMaid.languages || []}
                  fieldPath="languages"
                  type="multiselect"
                  options={languageOptions}
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Professional Information Section */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Professional Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Hourly Rate"
                  value={localMaid.hourlyRate}
                  fieldPath="hourlyRate"
                  type="number"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                  validation={{ required: true, min: 0, step: 0.25 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Years of Experience"
                  value={localMaid.experience}
                  fieldPath="experience"
                  type="number"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                  validation={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Experience Level"
                  value={localMaid.experienceLevel}
                  fieldPath="experienceLevel"
                  type="select"
                  options={experienceLevelOptions}
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Working Preference"
                  value={localMaid.workingPreference}
                  fieldPath="workingPreference"
                  type="select"
                  options={workingPreferenceOptions}
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12}>
                <InlineEditableField
                  label="Experience Description"
                  value={localMaid.experienceDescription}
                  fieldPath="experienceDescription"
                  type="text"
                  multiline={true}
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                  placeholder="Describe your cleaning experience..."
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Services & Areas Section */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Services & Areas
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <InlineEditableField
                  label="Services Offered"
                  value={localMaid.servicesOffered || localMaid.services || []}
                  fieldPath="services"
                  type="multiselect"
                  options={serviceOptions}
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12}>
                {serviceAreasError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {serviceAreasError}
                  </Alert>
                )}
                <InlineEditableField
                  label="Preferred Service Areas"
                  value={maidServiceAreas.map(area => area.name)}
                  fieldPath="serviceAreas"
                  type="multiselect"
                  options={serviceAreaOptions}
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode || loadingServiceAreas}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Max Travel Distance"
                  value={localMaid.maxTravelDistance || localMaid.maxTravelRadius}
                  fieldPath="maxTravelRadius"
                  type="number"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                  validation={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Legal & Capabilities Section */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Legal & Capabilities
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Work Authorized"
                  value={localMaid.workAuthorized}
                  fieldPath="workAuthorized"
                  type="boolean"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Background Check Consent"
                  value={localMaid.backgroundCheckConsent}
                  fieldPath="backgroundCheckConsent"
                  type="boolean"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Has Insurance"
                  value={localMaid.hasInsurance}
                  fieldPath="hasInsurance"
                  type="boolean"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Has Vehicle"
                  value={localMaid.hasVehicle}
                  fieldPath="hasVehicle"
                  type="boolean"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Brings Supplies"
                  value={localMaid.bringsSupplies}
                  fieldPath="bringsSupplies"
                  type="boolean"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              {localMaid.hasInsurance && (
                <>
                  <Grid item xs={12} md={6}>
                    <InlineEditableField
                      label="Insurance Provider"
                      value={localMaid.insuranceProvider}
                      fieldPath="insuranceProvider"
                      type="text"
                      maidId={localMaid.id}
                      onSave={handleFieldSave}
                      disabled={!editMode}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InlineEditableField
                      label="Policy Number"
                      value={localMaid.policyNumber}
                      fieldPath="policyNumber"
                      type="text"
                      maidId={localMaid.id}
                      onSave={handleFieldSave}
                      disabled={!editMode}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {/* Emergency Contact & Payment Section */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Emergency Contact & Payment
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Emergency Contact Name"
                  value={localMaid.emergencyContactName}
                  fieldPath="emergencyContact.name"
                  type="text"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Emergency Contact Phone"
                  value={localMaid.emergencyContactPhone}
                  fieldPath="emergencyContact.phone"
                  type="phone"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Emergency Contact Relationship"
                  value={localMaid.emergencyContactRelationship}
                  fieldPath="emergencyContact.relationship"
                  type="text"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Payment Method"
                  value={localMaid.paymentMethod}
                  fieldPath="paymentMethod"
                  type="text"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Bank Name"
                  value={localMaid.bankName}
                  fieldPath="bankName"
                  type="text"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InlineEditableField
                  label="Tax Classification"
                  value={localMaid.taxClassification}
                  fieldPath="taxClassification"
                  type="text"
                  maidId={localMaid.id}
                  onSave={handleFieldSave}
                  disabled={!editMode}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Onboarding Status Section */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <OnboardingStatusChip
                  maidProfile={localMaid}
                  variant="detailed"
                  onViewMissingFields={() => setShowMissingFields(!showMissingFields)}
                />
              </Grid>
              
              {/* Missing Fields Panel */}
              {showMissingFields && (
                <Grid item xs={12}>
                  <MissingFieldsPanel maidProfile={localMaid} />
                </Grid>
              )}
              
              {localMaid.onboardingStatus !== 'incomplete' && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Onboarding Agreements</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <Chip 
                      label="Terms of Service" 
                      color={localMaid.termsOfService ? 'success' : 'error'}
                      variant={localMaid.termsOfService ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip 
                      label="Privacy Policy" 
                      color={localMaid.privacyPolicy ? 'success' : 'error'}
                      variant={localMaid.privacyPolicy ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip 
                      label="Safety Guidelines" 
                      color={localMaid.safetyGuidelines ? 'success' : 'error'}
                      variant={localMaid.safetyGuidelines ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Chip 
                      label="Quality Standards" 
                      color={localMaid.qualityStandards ? 'success' : 'error'}
                      variant={localMaid.qualityStandards ? 'filled' : 'outlined'}
                      size="small"
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Comprehensive Onboarding Data Section */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Comprehensive Onboarding Data
            </Typography>
            {loadingComprehensive ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading comprehensive onboarding data...
                </Typography>
              </Box>
            ) : (
              <ComprehensiveOnboardingView 
                maidProfile={localMaid} 
                onboardingData={comprehensiveData} 
              />
            )}
          </Paper>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} startIcon={<Close />}>
          Close
        </Button>
        <Button
          variant="outlined"
          startIcon={<Settings />}
          onClick={handleEditDetails}
        >
          Edit Details
        </Button>
        <Button
          variant="outlined"
          color="info"
          startIcon={<PersonAdd />}
          onClick={handleCreateFakeCustomer}
          disabled={creatingCustomer || !localMaid}
        >
          {creatingCustomer ? 'Creating...' : 'Sync to Houcall Pro'}
        </Button>
        {localMaid && localMaid.onboardingStatus !== 'approved' && (
          <Button
            variant="contained"
            color="success"
            startIcon={<VerifiedUser />}
            onClick={handleVerifyMaid}
            disabled={verifyingMaid}
          >
            {verifyingMaid ? 'Verifying...' : 'Verify Technician'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditableMaidDetailsModal;